import { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronDown,
  ChevronRight,
  CalendarDays,
  ExternalLink,
  BookOpen,
  Headphones,
} from "lucide-react";
import "../../styles/Help.css";
import "../../styles/attendanceHelp.css";
import "../../styles/supportCenter.css";

const QUICK_ACTIONS = [
  { label: "Leave Dashboard", path: "/leave", desc: "Overview of leave status and balances" },
  { label: "Leave Balances", path: "/leave?tab=balances", desc: "View employee leave balance details" },
  { label: "Leave Settings", path: "/settings/leave-setup", desc: "Configure leave types, policies and allocations" },
  { label: "Apply for Leave", path: "/leave?tab=apply", desc: "Submit a new leave request" },
  { label: "Leave Approval", path: "/leave?tab=approval", desc: "Review and approve pending leave requests" },
  { label: "Holiday Calendar", path: "/holiday-calendar", desc: "View organisation holidays" },
  { label: "Leave Setup", path: "/settings/leave-setup", desc: "Configure leave policies and settings" },
  { label: "Leave Reports", path: "/reports/leave", desc: "Generate leave summary and detail reports" },
];

const LEAVE_TROUBLESHOOTING = [
  {
    problem: "Employee's leave balance shows zero for a leave type",
    check: "Leave balances are not auto-populated. Balances must be allocated — either manually via Leave Admin or through a leave policy with auto-allocation. If no allocation has been created for the employee and leave type, the balance will be zero.",
    action: "Go to Leave Admin and check if an allocation exists for the employee and leave type. If not, create one manually or assign a leave policy that includes that leave type with auto-allocation enabled.",
    path: "/settings/leave-setup",
    pathLabel: "Leave Settings",
  },
  {
    problem: "Leave balance does not match expected days after allocation",
    check: "The displayed balance reflects: Opening Balance + Accrued Days − Consumed Days. If the employee joined mid-year, the allocation may be prorated from their date of joining. Check the balance breakdown in the Balances tab.",
    action: "Go to Leave Balances and review the breakdown for the employee. Verify the opening balance, accrued days, and consumed days. If the numbers don't add up, check whether proration is enabled in the leave policy.",
    path: "/leave?tab=balances",
    pathLabel: "Leave Balances",
  },
  {
    problem: "Leave request rejected with 'Insufficient leave balance'",
    check: "The employee's available balance for the requested leave type is less than the number of days requested. This includes any pending (not yet approved) requests that may be holding days.",
    action: "Check the employee's current balance in Leave Balances. If the balance appears correct, verify there are no other pending requests consuming the same leave type. If the employee genuinely has no balance, the request will need to be applied as Loss of Pay (LOP) instead.",
    path: "/leave?tab=balances",
    pathLabel: "Leave Balances",
  },
  {
    problem: "Leave allocation failed — 'Organisation context missing'",
    check: "This error occurs when the system cannot determine which organisation to allocate leave for. This typically happens when the admin session has lost its organisation context.",
    action: "Log out and log back in to refresh your session. Then retry the allocation from Leave Admin. If the issue persists, check that your user profile is linked to an active organisation.",
    path: "/settings/leave-setup",
    pathLabel: "Leave Settings",
  },
  {
    problem: "Leave type not appearing when applying for leave",
    check: "Only leave types that have been configured and have a policy or allocation assigned to the employee will appear in the leave application form. If a leave type was created but not allocated, employees cannot apply for it.",
    action: "Go to Leave Admin and verify the leave type exists and that the employee has an active allocation or policy for it. Also check that the leave type is not marked as inactive.",
    path: "/settings/leave-setup",
    pathLabel: "Leave Settings",
  },
  {
    problem: "Leave request stuck in 'Pending' status — not reaching approver",
    check: "Leave requests follow an approval workflow: Employee Applies → Manager Review → HR Review → Final. If no approver is configured or the approver's role is not set correctly, the request may remain pending.",
    action: "Go to Leave Approval to check if the request appears in the approval queue. Verify that the employee's reporting manager is set correctly in their profile. Check the leave workflow settings in Leave Setup.",
    path: "/leave?tab=approval",
    pathLabel: "Leave Approval",
  },
  {
    problem: "Approved leave not reflecting in payroll as leave days",
    check: "Payroll pulls leave data for the pay period automatically. However, the leave must be in 'Approved' status and fall within the payroll period dates. Pending, rejected, or cancelled leaves are not included.",
    action: "Verify the leave status is 'Approved' (not Pending or Escalated) and the leave dates fall within the payroll period. If the leave was approved after payroll was processed, you may need to reprocess the payroll for that period.",
    path: "/leave",
    pathLabel: "Leave Dashboard",
  },
  {
    problem: "Holidays not appearing in the leave calendar",
    check: "The leave calendar shows holidays from your organisation's Holiday Calendar. If holidays have not been configured for the current year, they will not appear.",
    action: "Go to the Holiday Calendar page and verify that holidays are configured for the current year. If none exist, add them manually or import from a template.",
    path: "/holiday-calendar",
    pathLabel: "Holiday Calendar",
  },
  {
    problem: "Cannot create or edit leave types — fields appear required",
    check: "When creating or editing a leave type, certain fields such as leave code and leave name are mandatory. The form will not submit if required fields are empty or contain invalid values.",
    action: "Ensure all required fields (leave code, leave name, type) are filled. Check for validation errors highlighted on the form. If editing an existing type, make sure you have admin-level access.",
    path: "/settings/leave-setup",
    pathLabel: "Leave Settings",
  },
  {
    problem: "Employee cannot see their own leave balances or apply for leave",
    check: "Employees in self-service mode can access their own leave balances and apply for leave from the Leave Dashboard. If the page is not accessible, the employee's role or page access may not be configured correctly.",
    action: "Ask the employee to navigate to the Leave Dashboard. If they cannot access it, check their role (should be 'employee') and page access settings in Settings. Ensure the leave module is enabled for their access level.",
    path: "/leave",
    pathLabel: "Leave Dashboard",
  },
];

export default function LeaveSupportPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [openItem, setOpenItem] = useState(null);
  const resultsRef = useRef(null);

  const q = search.trim().toLowerCase();

  const filteredItems = useMemo(() => {
    if (!q) return LEAVE_TROUBLESHOOTING;
    return LEAVE_TROUBLESHOOTING.filter(
      (t) =>
        t.problem.toLowerCase().includes(q) ||
        t.check.toLowerCase().includes(q) ||
        t.action.toLowerCase().includes(q)
    );
  }, [q]);

  return (
    <div className="brix-page">
      {/* ── Header ── */}
      <div className="hs-hero hs-hero-compact">
        <div className="hs-hero-content">
          <div className="ah-hero-title-row">
            <div className="ah-hero-icon ic-teal">
              <CalendarDays size={28} strokeWidth={1.8} />
            </div>
            <div>
              <h1>Leave Balance</h1>
              <p className="hs-hero-sub">
                Troubleshoot leave balance discrepancies, allocation issues, and leave workflow problems.
              </p>
            </div>
          </div>

          <form
            className="hs-search-wrap"
            onSubmit={(e) => {
              e.preventDefault();
              if (q && resultsRef.current) resultsRef.current.scrollIntoView({ behavior: "smooth" });
            }}
          >
            <div className="hs-search-input-wrap">
              <Search size={16} className="hs-search-icon" />
              <input
                type="text"
                className="hs-search-input"
                placeholder="Search leave issues — e.g. balance, allocation, approval..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="submit" className="hs-search-btn">Search</button>
          </form>
        </div>
      </div>

      <div ref={resultsRef}>
        {/* ── Quick Actions ── */}
        <div style={{ marginBottom: 28 }}>
          <div className="hs-section-head">
            <h2 className="hs-section-title">Quick Actions</h2>
            <p className="hs-section-sub">Jump to the relevant page to check or fix your issue</p>
          </div>
          <div className="sc-issues-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
            {QUICK_ACTIONS.map((link) => (
              <div
                key={link.path}
                className="sc-issue-card"
                role="button"
                tabIndex={0}
                style={{ cursor: "pointer" }}
                onClick={() => navigate(link.path)}
                onKeyDown={(e) => e.key === "Enter" && navigate(link.path)}
              >
                <div className="sc-issue-name" style={{ fontSize: "0.88rem" }}>{link.label}</div>
                <div className="sc-issue-desc" style={{ fontSize: "0.78rem" }}>{link.desc}</div>
                <div className="sc-issue-link">
                  Open <ExternalLink size={12} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Leave Balance Troubleshooting (main section) ── */}
        <div style={{ marginBottom: 28 }}>
          <div className="hs-section-head">
            <h2 className="hs-section-title">Leave Balance Troubleshooting</h2>
            <p className="hs-section-sub">
              Select your issue to see what to check and how to resolve it
            </p>
          </div>

          {q && filteredItems.length === 0 && (
            <div className="sc-no-results">
              No leave issues match &ldquo;<strong>{search}</strong>&rdquo;. Try a different keyword.
            </div>
          )}

          <div className="sc-accordion-list">
            {filteredItems.map((item, idx) => {
              const isOpen = openItem === idx;
              return (
                <div key={item.problem} className="hs-faq-item">
                  <button
                    type="button"
                    className="hs-faq-btn"
                    onClick={() => setOpenItem(isOpen ? null : idx)}
                  >
                    <span className="hs-faq-q">{item.problem}</span>
                    <ChevronDown size={16} className={`hs-faq-chevron${isOpen ? " open" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="hs-faq-answer">
                          <div style={{ marginBottom: 10 }}>
                            <strong style={{ color: "#64748b", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Check
                            </strong>
                            <p style={{ margin: "4px 0 0" }}>{item.check}</p>
                          </div>
                          <div style={{ marginBottom: item.path ? 12 : 0 }}>
                            <strong style={{ color: "#15803d", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Next Action
                            </strong>
                            <p style={{ margin: "4px 0 0" }}>{item.action}</p>
                          </div>
                          {item.path && (
                            <button
                              type="button"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "6px 14px",
                                fontSize: "0.82rem",
                                fontWeight: 500,
                                color: "#1a437d",
                                background: "rgba(26,67,125,0.06)",
                                border: "1px solid rgba(26,67,125,0.15)",
                                borderRadius: 6,
                                cursor: "pointer",
                              }}
                              onClick={() => navigate(item.path)}
                            >
                              {item.pathLabel}
                              <ExternalLink size={13} />
                            </button>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Need detailed leave information? ── */}
        <div className="sc-col-card" style={{ marginBottom: 28 }}>
          <div className="sc-info-row">
            <div className="sc-col-header-icon sc-icon-blue">
              <BookOpen size={18} strokeWidth={2} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 className="hs-section-title">Need detailed leave information?</h2>
              <p className="sc-info-row-desc">
                Visit the Leave Help Guide for detailed documentation, FAQs and step-by-step leave setup instructions.
              </p>
            </div>
            <button
              type="button"
              className="sc-info-row-btn"
              onClick={() => navigate("/help/leave")}
            >
              Open Leave Help Guide
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* ── Still facing an issue? ── */}
        <div className="sc-contact-card" style={{ marginBottom: 28 }}>
          <div className="sc-contact-inner sc-contact-row">
            <div className="sc-contact-icon">
              <Headphones size={24} strokeWidth={1.5} />
            </div>
            <div className="sc-contact-text">
              <h2 className="sc-contact-title">Still facing an issue?</h2>
              <p className="sc-contact-sub">
                If the available troubleshooting steps don't resolve the problem, contact our support team for further assistance.
              </p>
            </div>
            <button
              type="button"
              className="sc-contact-btn"
              onClick={() => navigate("/support")}
            >
              <Headphones size={15} />
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
